import { Component, OnInit } from "@angular/core";
import { ViewController, NavParams } from "ionic-angular";
import { DiaprofilPatient } from "../../app/models/diaprofil-patient";
import { DiaprofilProTask } from "../../app/models/diaprofil-pro-task";
import { WidgetConfig } from "@zitelab/ionic-fhir-questionnaires/dist/models/widget";
import {
  QuestionnaireService,
  QuestionnaireState
} from "@zitelab/ionic-fhir-questionnaires";
import { FhirService } from "../../app/services/fhir.service";
import { DIAPROFIL_QUESTIONNAIRE_UUID } from "../../app/models/types";
import * as QuestionnaireActions from "@zitelab/ionic-fhir-questionnaires/dist/";
import { diaprofilEnableWhenFunctions } from "../../app/utils/enable-when-functions";
import { DashboardService } from "../../app/services/dashboard.service";
import { TranslateService } from "@ngx-translate/core";
import { compare } from "fast-json-patch";
import { ToastController } from "ionic-angular/components/toast/toast-controller";
import { _i18n_str } from "../../assets/i18n/non-static-ids";
import * as moment from "moment";

@Component({
  selector: "patch-pro-modal",
  templateUrl: "patch-pro.modal.html"
})
export class PatchProModal implements OnInit {
  patient: DiaprofilPatient;
  proTask: DiaprofilProTask;
  widgets: WidgetConfig<any>[] = [];
  questionnaireResource: fhir.Questionnaire;
  state: QuestionnaireState | undefined;
  responseExtensions: { url: string; [key: string]: any }[];
  private enableWhenFunctions: {
    [key: string]: (formAnswers: { [key: string]: any }) => boolean;
  } = diaprofilEnableWhenFunctions;
  /**
   * Used to provide new updated date to
   * https://fhir.zitelab.eu/extensions/hestia-questionnaire-response-start-time on patch
   */
  private openModalDateTime: string;
  constructor(
    public viewCtrl: ViewController,
    public navParams: NavParams,
    public translate: TranslateService,
    public questionnaireService: QuestionnaireService,
    public dashboard: DashboardService,
    public fhir: FhirService,
    public toastCtrl: ToastController
  ) {
    this.patient = (navParams.get("patient") || {}) as DiaprofilPatient;
    this.proTask = (navParams.get("proTask") || null) as DiaprofilProTask;
    this.questionnaireResource = this.fhir.resolveReference(
      `Questionnaire/${DIAPROFIL_QUESTIONNAIRE_UUID}`
    ) as fhir.Questionnaire;
    this.widgets = this.dashboard.findWidgetsForResponse(
      this.questionnaireResource,
      this.proTask.flattenedAnswers,
      this.proTask.task.id
    );
    this.responseExtensions = this.proTask.response.extension;
    this.state = this.getState();
    if (this.state === undefined) {
      this.questionnaireService.store.dispatch(
        new QuestionnaireActions.InitAction({
          questionnaire: this.questionnaireResource,
          taskId: this.proTask.task.id,
          procedureRequestId: this.proTask.procedureRequest.id,
          patientId: this.patient.fhirResource.id,
          subjectRef: this.proTask.response.subject,
          authorRef: this.proTask.response.author,
          sourceRef: this.proTask.response.source,
          localVariables: {},
          values: this.proTask.flattenedAnswers,
          questionnaireResponse: this.proTask.response
        })
      );
      this.state = this.getState();
    }
  }

  ngOnInit(): void {
    this.openModalDateTime = moment(new Date()).toISOString(true);
  }

  getState() {
    return this.questionnaireService.questionnaires.find(x => {
      return (
        x.questionnaire.id === this.questionnaireResource.id &&
        x.questionnaire.meta.versionId ===
          this.questionnaireResource.meta.versionId &&
        x.taskId === this.proTask.task.id
      );
    });
  }

  dismiss($event) {
    this.viewCtrl.dismiss();
  }

  handleFormChange(form) {
    const cleanedForm = Object.keys(form).reduce((prev, cur, i) => {
      if (form[cur] !== null && form[cur] !== "") {
        if (Array.isArray(form)) {
          if (form.length > 0) {
            prev[cur] = form[cur];
          }
        } else {
          prev[cur] = form[cur];
        }
      }
      return prev;
    }, {});
    const allCleanValues = { ...this.state!.values, ...cleanedForm };
    const requiredWidgets = this.widgets.filter(widget => {
      return (
        widget.displayOnly === false &&
        widget.isShown(allCleanValues, this.enableWhenFunctions)
      );
    });
    const updatePayload = {
      questionnaire: this.questionnaireResource,
      taskId: this.proTask.task.id,
      procedureRequestId: this.proTask.procedureRequest.id,
      patientId: this.patient.fhirResource.id,
      subjectRef: this.proTask.response.subject,
      authorRef: this.proTask.response.author,
      sourceRef: this.proTask.response.source,
      values: allCleanValues,
      progress:
        (Object.keys(allCleanValues).length / requiredWidgets.length) * 100,
      latestAnswerDate: new Date(),
      questionnaireResponse: this.state.questionnaireResponse // The preexisting questionnaireResponse
    };
    this.questionnaireService.store.dispatch(
      new QuestionnaireActions.UpdateAction({
        ...updatePayload,
        questionnaireResponse: this.questionnaireService.createQuestionnaireResponse(
          updatePayload,
          this.responseExtensions
        )
      })
    );
  }

  handleFormSubmit(event, form) {
    event.preventDefault();
    const cleanedForm = Object.keys(form).reduce((prev, cur, i) => {
      if (form[cur] !== null && form[cur] !== "") {
        if (Array.isArray(form)) {
          if (form.length > 0) {
            prev[cur] = form[cur];
          }
        } else {
          prev[cur] = form[cur];
        }
      }
      return prev;
    }, {});
    const allCleanValues = { ...this.state!.values, ...cleanedForm };
    const requiredWidgets = this.widgets.filter(widget => {
      return (
        widget.displayOnly === false &&
        widget.isShown(allCleanValues, this.enableWhenFunctions)
      );
    });
    const updatePayload = {
      questionnaire: this.questionnaireResource,
      taskId: this.proTask.task.id,
      procedureRequestId: this.proTask.procedureRequest.id,
      patientId: this.patient.fhirResource.id,
      subjectRef: this.proTask.response.subject,
      authorRef: this.proTask.response.author,
      sourceRef: this.proTask.response.source,
      values: allCleanValues,
      progress:
        (Object.keys(allCleanValues).length / requiredWidgets.length) * 100,
      latestAnswerDate: new Date(),
      questionnaireResponse: this.state.questionnaireResponse // The preexisting questionnaireResponse
    };
    const newResponse = this.questionnaireService.createQuestionnaireResponse(
      updatePayload,
      this.responseExtensions.map(ext => {
        // We update the response start time
        if (
          ext.url ===
          "https://fhir.zitelab.eu/extensions/hestia-questionnaire-response-start-time"
        ) {
          return {
            ...ext,
            valueDateTime: this.openModalDateTime
          };
        } else {
          return ext;
        }
      })
    );
    this.questionnaireService.store.dispatch(
      new QuestionnaireActions.UpdateAction({
        ...updatePayload,
        questionnaireResponse: newResponse
      })
    );
    const mergedResponse = {
      ...this.proTask.response,
      ...{
        item: newResponse.item,
        meta: newResponse.meta,
        status: "amended",
        extension: newResponse.extension
      }
    };
    const patchContent = compare(this.proTask.response, mergedResponse);
    this.fhir
      .patchResource({
        resourceType: "QuestionnaireResponse",
        resourceId: mergedResponse.id,
        operations: patchContent
      })
      .subscribe(
        result => {
          const toast = this.toastCtrl.create({
            message: this.translate.instant(
              _i18n_str("PATCH_PRO_MODAL.SUCCESS_TOAST")
            ),
            duration: 3000,
            position: "top",
            closeButtonText: "Luk",
            showCloseButton: true
          });
          this.viewCtrl.dismiss({ editedResponse: mergedResponse });
        },
        error => {
          console.log(error);
          const toast = this.toastCtrl.create({
            message: this.translate.instant(error.message),
            duration: 3000,
            position: "top",
            closeButtonText: "Luk",
            showCloseButton: true
          });
        }
      );
  }

  toPreviousPage(event) {}
}



// WEBPACK FOOTER //
// ./src/modals/patch-pro.modal/patch-pro.modal.ts